import React, { useEffect, useMemo, useRef, useState } from 'react'
import Container from './container'
import styles from './investments-table.module.css'
import Filter from './filter'
import Icon from './icons'
import { Link } from 'gatsby'
import * as start from '../pages/content-hub/start.module.css'
import filterStyles from './filter.module.css'
import { cn } from '../lib/helpers'

const extractExcerptText = (rawExcerpt) => {
  if (!Array.isArray(rawExcerpt)) return ''
  const firstBlock = rawExcerpt.find(block => block?._type === 'block' && Array.isArray(block.children))
  if (!firstBlock) return ''
  return firstBlock.children
    .filter(child => typeof child?.text === 'string')
    .map(child => child.text)
    .join('')
}

const getStageOptions = (companies) => {
  const unique = new Map()
  companies.forEach((company) => {
    if (!company?.stage) return
    company.stage.forEach(stage => {
      if (!stage?.title) return
      if (!unique.has(stage.title)) {
        unique.set(stage.title, { id: stage.title, title: stage.title })
      }
    })
  })
  return Array.from(unique.values()).sort((a, b) => a.title.localeCompare(b.title))
}

function InvestmentsTable ({ name, content }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [filtering, setFiltering] = useState({ stage: 'all' })
  const [openFilter, setOpenFilter] = useState(null)
  const [isSticky, setIsSticky] = useState(false)
  const [animateTitle, setAnimateTitle] = useState(false)
  const sentinelRef = useRef(null)
  const sectionRef = useRef(null)
  const hasIntersectedRef = useRef(false)
  const lastScrollYRef = useRef(0)
  const companies = useMemo(() => {
    if (!Array.isArray(content)) return []
    return content
      .map(item => item?.company)
      .filter(Boolean)
      .filter(company => company?.slug?.current)
      .sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }))
  }, [content])

  const stageOptions = useMemo(() => getStageOptions(companies), [companies])

  const handleOpenFilter = (filter) => {
    setOpenFilter(prevFilter => (prevFilter === filter ? null : filter))
  }

  const handleFilter = (filter) => {
    setFiltering(prevFilters => ({
      ...prevFilters,
      [filter[0]]: filter[1]
    }))
  }

  const handleSearch = (event) => {
    setSearchQuery(event.target.value.toLowerCase())
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openFilter && !event.target.closest(`.${filterStyles.filter}`)) {
        setOpenFilter(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openFilter])

  useEffect(() => {
    if (!sentinelRef.current) return
    const observerOptions = {
      root: null,
      threshold: [1],
      rootMargin: '0px 0px -16px 0px'
    }
    // eslint-disable-next-line no-undef
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (sectionRef.current) {
          const rect = sectionRef.current.getBoundingClientRect()
          const inView = rect.bottom > 0 && rect.top < window.innerHeight
          if (!inView) {
            setIsSticky(false)
            setAnimateTitle(false)
            return
          }
        }

        const currentScrollY = window.scrollY || 0
        const scrollingDown = currentScrollY > lastScrollYRef.current
        lastScrollYRef.current = currentScrollY

        if (!hasIntersectedRef.current && entry.isIntersecting) {
          hasIntersectedRef.current = true
        }

        if (!hasIntersectedRef.current) {
          return
        }

        if (entry.intersectionRatio < 1) {
          setAnimateTitle(true)
          setIsSticky(true)
          return
        }

        // Sentinel fully visible (header at top). If re-entering from above,
        // keep title large without animation; if coming from below, animate up.
        setAnimateTitle(!scrollingDown)
        setIsSticky(false)
      })
    }, observerOptions)

    observer.observe(sentinelRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  const filteredCompanies = companies.filter(company => {
    const stageMatch = filtering.stage === 'all' || company.stage?.some(stage => stage.title === filtering.stage)
    const searchMatch = company.title?.toLowerCase().includes(searchQuery)
    return stageMatch && searchMatch
  })

  const headerClassName = `${styles.filteringSearch} ${isSticky ? styles.sticky : ''} ${animateTitle ? styles.animateTitle : styles.noAnimateTitle}`

  return (
    <Container size='large'>
      <section ref={sectionRef} className={`${styles.investmentsTeamSubpage}`} id="companies">
        <div ref={sentinelRef} className={cn('sentinel', isSticky && styles.sticky)} />
        <section className={headerClassName}>
          <h2 className={styles.title}>{name && `${name}’s `}Investments</h2>
          <div className={styles.filtringWrapper}>
            <div className={styles.headerCompany}>Company Name</div>
            <div className={styles.headerDescription}>Description</div>
            <div className={styles.headerStage}>
              <Filter
                title='Stages'
                options={stageOptions}
                filter='stage'
                filterHandler={handleFilter}
                isOpen={openFilter === 'stage'}
                handleOpenFilter={state => handleOpenFilter(state)}
              />
            </div>
            <div className={`${start.searchBoxWhite} ${styles.search}`}>
              <form onSubmit={(e) => e.preventDefault()}>
                <Icon symbol='search' />
                <input
                  type='text'
                  placeholder='Search'
                  value={searchQuery}
                  onChange={handleSearch}
                />
              </form>
            </div>
          </div>
        </section>

        <div className={styles.listWrap}>
          {filteredCompanies.length > 0 ? (
            filteredCompanies.map((company) => (
              <div key={company._id || company.slug.current} className={styles.listItem}>
                <Link to={`/companies/${company.slug.current}/`} className={styles.listLink}>
                  <span className={styles.companyTitle}>{company.title}</span>
                  <span className={styles.companyDescription}>
                    {extractExcerptText(company._rawExcerpt)}
                  </span>
                  <span className={styles.companyStage}>
                    {company?.stage && company.stage[0] ? company.stage[0].title : ''}
                  </span>
                  <span className={styles.companyArrowIcon} />
                </Link>
              </div>
            ))
          ) : (
            <p className={styles.noResults}>No results found</p>
          )}
        </div>
      </section>
    </Container>
  )
}

export default InvestmentsTable
